<template>
  <v-container>
    <v-overlay :value="overlay" class="text-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
      <br />
      {{ loadingText }} {{ "(" + average + "%)" }}
    </v-overlay>
    <iframe id="my_iframe" style="display:none;"></iframe>
    <v-row>
      <v-col>
        <HeaderActivity
          @modify="modify()"
          @cancelModify="cancelModify()"
          @saveModify="saveModify"
          @delete="deleteActivity"
          :modifying="modifying"
          :difficulte="thisActivity.difficulte"
          :username="thisActivity.userName"
          :is-mine="thisActivity.isMine"
          :titre="thisActivity.titre"
          :comments="thisActivity.comments"
        ></HeaderActivity>
      </v-col>
    </v-row>
    <v-card elevation="0" class="margin-card">
      <span v-if="thisActivity.description">
        <v-card-title>{{ $t("aimOfActivity") }}</v-card-title>
        <v-card-text>
          <v-card-subtitle v-if="!modifying">{{
            thisActivity.description
          }}</v-card-subtitle>
          <v-text-field
            v-if="modifying"
            prepend-icon="mdi-head-cog-outline"
            required
            v-model="thisActivity.description"
            :rules="basicRules"
          ></v-text-field>
        </v-card-text>
      </span>
    </v-card>
    <v-card elevation="0" class="margin-card">
      <v-card-title>{{ $t("generalDescription") }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <h4>{{ $t("cognitiveCapacity") }}</h4>
            <span v-if="thisActivity.fonctionsCognitives && !modifying">
              <v-chip
                class="mr-2"
                v-for="fonctionCognitive of thisActivity.fonctionsCognitives"
                :key="fonctionCognitive"
              >
                {{ fonctionCognitive }}
              </v-chip>
            </span>
            <v-autocomplete
              v-if="modifying"
              required
              filled
              chips
              color="blue-grey lighten-2"
              v-model="thisActivity.fonctionsCognitives"
              :items="fonctions"
              multiple
              item-text="name"
              item-value="name"
              :rules="basicRules"
            ></v-autocomplete>
          </v-col>
          <v-col>
            <h4>{{ $t("procedures") }}</h4>
            <span v-if="thisActivity.proceduresUtilisees && !modifying">
              <v-chip
                class="mr-2"
                v-for="procedureUtilisee of thisActivity.proceduresUtilisees"
                :key="procedureUtilisee"
              >
                {{ procedureUtilisee }}
              </v-chip>
            </span>
            <v-autocomplete
              v-if="modifying"
              required
              filled
              chips
              color="blue-grey lighten-2"
              v-model="thisActivity.proceduresUtilisees"
              :items="procedures"
              multiple
              item-text="name"
              item-value="name"
              :rules="basicRules"
            >
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  Il s’agit ici de renseigner la procédure de rééducation auquel
                  votre activité fait référence.
                  <br />
                  Trois choix vous sont proposés :
                  <br />
                  <ul>
                    <li>
                      <b>Amélioration des fonctions déficitaires</b> : Ici il
                      s’agit ici de soutenir, entrainer, répéter certaines
                      cibles cognitives spécifiques en lien avec des
                      modélisations théoriques.
                    </li>
                    <li>
                      <b>Optimisation des fonctions préservées</b> : ici il
                      s’agit d’optimiser les fonctions préservées dans le but
                      d’effectuer une réorganisation fonctionnelle à l’aide de
                      stratégies compensatoires.
                    </li>
                    <li>
                      <b>Aménagement de l’environnement</b> : il s’agira ici de
                      limiter les répercussions dans la vie quotidienne à l’aide
                      d’une prothèse mentale.
                    </li>
                  </ul>
                </v-tooltip>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h4>{{ $t("typeProcessing") }}</h4>
            <v-chip v-if="!modifying" class="text-uppercase">{{
              thisActivity.typeTraitement
            }}</v-chip>
            <v-btn-toggle
              v-if="modifying"
              prepend-icon="mdi-swap-vertical-circle-outline"
              v-model="thisActivity.typeTraitement"
              mandatory
              dense
              id="type-traitement"
              required
            >
              <v-btn value="bottom-up">
                Bottom-up
              </v-btn>
              <v-btn value="top-down">
                Top-down
              </v-btn>
              <v-btn value="bottom-up et top-down">
                Bottom-up et Top-down
              </v-btn>
            </v-btn-toggle>
            <template v-if="modifying">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Deux approches principales sont proposées : <br />
                <ul>
                  <li>
                    <b>« Top-down »</b> : Il s’agit d’amener la personne à
                    utiliser les informations sur son propre fonctionnement afin
                    d’influencer sa perception (ex. métacognition).
                  </li>
                  <li>
                    <b>« Bottom-up »</b> : Ces stratégies ont pour but la
                    manipulation et la répétition des informations pour générer
                    des représentations cognitives élaborées et de restaurer les
                    fonctions déficitaires.
                  </li>
                </ul>
              </v-tooltip>
            </template>
          </v-col>
          <v-col>
            <h4>{{ $t("duration") }}</h4>
            <span v-if="!modifying">
              {{ thisActivity.duree }} minute{{
                thisActivity.duree > 1 ? "s" : ""
              }}
            </span>
            <v-text-field
              v-if="modifying"
              prepend-icon="mdi-clock-outline"
              required
              v-model="thisActivity.duree"
              type="number"
              min="0"
              :rules="basicRules"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h4>{{ $t("numberOfSession") }}</h4>
            <span v-if="!modifying">
              {{ thisActivity.nombreSeances }}
            </span>

            <v-text-field
              v-if="modifying"
              prepend-icon="mdi-fast-forward-10"
              required
              v-model="thisActivity.nombreSeances"
              type="number"
              min="0"
              :rules="basicRules"
            ></v-text-field>
          </v-col>
          <v-col>
            <h4>{{ $t("typeOfIntervention") }}</h4>
            <v-chip v-if="!modifying" class="text-capitalize">
              <span>
                {{ thisActivity.typeIntervention }}
              </span>
            </v-chip>
            <v-btn-toggle
              v-if="modifying"
              v-model="thisActivity.typeIntervention"
              mandatory
              dense
              id="type-intervention"
              required
            >
              <v-btn value="individuel">
                {{ $t("individual") }}
              </v-btn>
              <v-btn value="groupal">
                {{ $t("group") }}
              </v-btn>
              <v-btn value="individuel et groupal">
                {{ $t("individualAndGroup") }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h4>{{ $t("age") }}</h4>
            <v-chip v-if="!modifying"> {{ thisActivity.age }} </v-chip>
            <v-autocomplete
              v-if="modifying"
              prepend-icon="mdi-cake"
              required
              filled
              chips
              color="blue-grey lighten-2"
              v-model="thisActivity.age"
              :items="tranchesAges"
              :rules="basicRules"
            ></v-autocomplete>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card elevation="0" class="margin-card" v-if="thisActivity.materiel">
      <v-card-title>{{ $t("material") }}</v-card-title>
      <v-card-text>
        <v-col v-if="!this.modifying">
          <span
            v-html="thisActivity.materiel.replaceAll(/\n/g, '<br />')"
          ></span>
        </v-col>
        <v-col v-else>
          <v-textarea
            prepend-icon="mdi-progress-wrench"
            label="Matériel(s) utilisé(s)"
            required
            v-model="thisActivity.materiel"
            :rules="basicRules"
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Veuillez lister le matériel nécessaire à la réalisation de votre
                activité.
              </v-tooltip>
            </template>
          </v-textarea>
        </v-col>
      </v-card-text>
    </v-card>
    <v-card
      elevation="0"
      v-if="thisActivity.etapes && thisActivity.etapes[0] !== ''"
    >
      <v-card-title>{{ $t("step") }}</v-card-title>
      <v-card-text>
        <div v-if="!this.modifying">
          <v-row v-for="(etape, index) in thisActivity.etapes" :key="index">
            <v-col>
              <h4>Étape(s) {{ index + 1 }}</h4>
              <span v-html="etape.replaceAll(/\n/g, '<br />')"></span>
            </v-col>
          </v-row>
        </div>

        <div v-else>
          <v-row>
            <v-col>
              <v-textarea
                v-for="(etape, index) in thisActivity.etapes"
                :key="index"
                prepend-icon="mdi-order-bool-ascending-variant"
                :label="'Étape ' + (index + 1)"
                required
                v-model="thisActivity.etapes[index]"
                :append-outer-icon="
                  index === 0 && thisActivity.etapes.length < 10
                    ? 'mdi-plus'
                    : index != 0
                    ? 'mdi-minus'
                    : ''
                "
                @click:append-outer="
                  index === 0 && thisActivity.etapes.length < 10
                    ? addUser(thisActivity.etapes.length)
                    : index != 0
                    ? deleteUser(index)
                    : ''
                "
                maxlength="500"
                counter
                :rules="basicRules"
              >
                <template v-if="index === 0" v-slot:prepend-inner>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    Vous devez ici renseigner la procédure pas à pas de votre
                    activité dans le but de fournir un protocole clair et
                    accessible. Veuillez essayer de faire apparaitre une
                    démarche de progressivité en termes de difficulté.
                  </v-tooltip>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
    <v-card elevation="0" class="margin-card" v-if="thisActivity.moyensMesure">
      <v-card-title>{{ $t("measure") }}</v-card-title>
      <v-card-text>
        <v-col>
          <span
            v-if="!this.modifying"
            v-html="thisActivity.moyensMesure.replaceAll(/\n/g, '<br />')"
          ></span>
          <v-textarea
            v-else
            prepend-icon="mdi-poll-box-outline"
            label="Ligne de base"
            required
            v-model="thisActivity.moyensMesure"
            :rules="basicRules"
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Fournir un moyen de mesurer les progressions de votre patient
                est toujours plus appréciable en termes d’objectivation (ex.
                grille de progression, pourcentage de réussite etc.).
              </v-tooltip>
            </template>
          </v-textarea>
        </v-col>
      </v-card-text>
    </v-card>
    <v-card elevation="0" class="margin-card" v-if="thisActivity.precautions">
      <v-card-title>{{ $t("caution") }}</v-card-title>
      <v-card-text>
        <v-col>
          <span
            v-if="!this.modifying"
            v-html="thisActivity.precautions.replaceAll(/\n/g, '<br />')"
          ></span>
          <v-textarea
            v-else
            prepend-icon="mdi-alert-outline"
            label="Precautions/Contres-indications"
            required
            v-model="thisActivity.precautions"
            :rules="basicRules"
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Veuillez renseigner ici les éventuelles contre-indications ou
                précautions à prendre avec votre activité.
              </v-tooltip>
            </template>
          </v-textarea>
        </v-col>
      </v-card-text>
    </v-card>
    <v-card
      elevation="0"
      class="margin-card"
      v-if="thisActivity.gestionCapicitesTransfert"
    >
      <v-card-title>{{ $t("transferManagement") }}</v-card-title>
      <v-card-text>
        <v-col>
          <span
            v-if="!this.modifying"
            v-html="
              thisActivity.gestionCapicitesTransfert.replaceAll(/\n/g, '<br />')
            "
          ></span>
          <v-textarea
            prepend-icon="mdi-swap-horizontal"
            label="Gestion des capacités de transfert"
            v-model="thisActivity.gestionCapicitesTransfert"
            v-else
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Il s’agit ici de renseigner ou de porter une réflexion sur les
                moyens de transferts horizontaux et verticaux possibles d’une
                activité. Le but étant la généralisation des acquis dans la vie
                quotidienne.
              </v-tooltip>
            </template>
          </v-textarea>
        </v-col>
      </v-card-text>
    </v-card>
    <v-card
      elevation="0"
      class="margin-card"
      v-if="thisActivity.cadreTheorique"
    >
      <v-card-title>{{ $t("theoreticalFramework") }}</v-card-title>
      <v-card-text>
        <v-col>
          <span
            v-if="!this.modifying"
            v-html="thisActivity.cadreTheorique.replaceAll(/\n/g, '<br />')"
          ></span>
          <v-textarea
            prepend-icon="mdi-pencil-outline"
            label="Cadre théorique"
            v-model="thisActivity.cadreTheorique"
            v-else
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                Il est préférable de renseigner les fondements théoriques de
                l’activité s’ils sont existants.
              </v-tooltip>
            </template>
          </v-textarea>
        </v-col>
      </v-card-text>
    </v-card>
    <v-card
      elevation="0"
      class="margin-card"
      v-if="
        (thisActivity.sourcesBibliographique &&
          thisActivity.sourcesBibliographique.length > 0) ||
          this.modifying
      "
    >
      <v-card-title>{{ $t("bibliographic") }}</v-card-title>
      <v-card-text>
        <v-col>
          <v-row v-if="!this.modifying">
            <v-col>
              <v-row
                v-for="(source, index) in thisActivity.sourcesBibliographique"
                :key="index"
              >
                <v-col>
                  <v-icon>{{ getIconByType(source.type) }}</v-icon
                  ><span v-html="urlify(source.source)" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <div v-else>
            <v-row>
              <v-col>
                <div class="text-center">
                  <v-btn
                    disabled
                    x-small
                    color="blue-grey"
                    class="ma-2 white--text"
                    fab
                    elevation="0"
                  >
                    <v-icon dark>
                      mdi-plus
                    </v-icon>
                  </v-btn>
                  <v-icon>
                    mdi-arrow-right
                  </v-icon>
                  <v-btn
                    class="ma-2 white--text"
                    color="blue-grey"
                    @click="addSource('article')"
                  >
                    Article
                  </v-btn>

                  <v-btn
                    color="blue-grey"
                    class="ma-2 white--text"
                    @click="addSource('livre')"
                  >
                    Livre
                  </v-btn>

                  <v-btn
                    class="ma-2 white--text"
                    color="blue-grey"
                    @click="addSource('chapitre')"
                  >
                    Chapitre de livre
                  </v-btn>
                  <v-btn
                    class="ma-2 white--text"
                    color="blue-grey"
                    @click="addSource('thèse')"
                  >
                    Thèse de doctorat
                  </v-btn>
                  <v-btn
                    class="ma-2 white--text"
                    color="blue-grey"
                    @click="addSource('mémoire')"
                  >
                    Mémoire
                  </v-btn>
                  <v-btn
                    class="ma-2 white--text"
                    color="blue-grey"
                    @click="addSource('internet')"
                  >
                    Page internet
                  </v-btn>
                </div>
              </v-col>
            </v-row>
            <v-row
              v-for="(source, index) in thisActivity.sourcesBibliographique"
              :key="index"
            >
              <v-col class="text-center">
                <v-text-field
                  :prepend-icon="getIconByType(source.type)"
                  label="Sources bibliographiques (normes APA)*"
                  v-model="thisActivity.sourcesBibliographique[index].source"
                  :append-outer-icon="
                    displayDesignButton(source.type) ? '' : 'mdi-delete'
                  "
                  required
                  :rules="basicRules"
                  @click:append-outer="
                    displayDesignButton(source.type) ? '' : deleteSource(index)
                  "
                />
              </v-col>
              <v-col cols="4" v-if="displayDesignButton(source.type)">
                <v-autocomplete
                  label="Design de l'étude"
                  required
                  filled
                  chips
                  color="blue-grey lighten-2"
                  v-model="thisActivity.sourcesBibliographique[index].design"
                  :items="designs"
                  :rules="basicRules"
                  :append-outer-icon="'mdi-delete'"
                  @click:append-outer="deleteSource(index)"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="subtitle-2 font-italic text--secondary">
                *L’ensemble des sources utilisées dans la conception de
                l’activité devrons être citées. Celles-ci devront respecter les
                normes de l’American Psychological Association (APA).
              </v-col>
            </v-row>
            <v-row
              v-if="
                thisActivity.sourcesBibliographique.filter(
                  e => e.type === 'article'
                ).length > 0
              "
            >
              <v-col>
                <v-alert class="apa-norm" border="right" color="blue-grey" dark>
                  <div
                    class="text-subtitle-1 text--secondary"
                    style="color:white !important;"
                  >
                    Article de revue
                  </div>
                  <div
                    class="text-subtitle-2 font-italic text--secondary"
                    style="color:white !important;"
                  >
                    Nom de l’auteur·e, Initiale. (Date). Titre de l’article. Nom
                    de la revue, volume(numéro), plage de pages. DOI ou URL
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <v-row
              v-if="
                thisActivity.sourcesBibliographique.filter(
                  e => e.type === 'livre'
                ).length > 0
              "
            >
              <v-col>
                <v-alert border="right" color="blue-grey" dark class="apa-norm">
                  <div
                    class="text-subtitle-1 text--secondary"
                    style="color:white !important;"
                  >
                    Livre
                  </div>
                  <div
                    class="text-subtitle-2 font-italic text--secondary"
                    style="color:white !important;"
                  >
                    Nom de l’auteur·e, Initiale. (Date). Titre du livre
                    (édition, volume). Nom de la maison d’édition. DOI ou URL
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <v-row
              v-if="
                thisActivity.sourcesBibliographique.filter(
                  e => e.type === 'chapitre'
                ).length > 0
              "
            >
              <v-col>
                <v-alert border="right" color="blue-grey" dark class="apa-norm">
                  <div
                    class="text-subtitle-1 text--secondary"
                    style="color:white !important;"
                  >
                    Chapitre
                  </div>
                  <div
                    class="text-subtitle-2 font-italic text--secondary"
                    style="color:white !important;"
                  >
                    Nom de l’auteur·e, Initiale. (Date). Titre du chapitre. Dans
                    Initiale. Nom de l’auteur·e & Initiale. Nom de l’auteur·e
                    (dirs.), Titre du livre (pp. plage de pages du chapitre).
                    Nom de la maison d’édition. DOI ou URL
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <v-row
              v-if="
                thisActivity.sourcesBibliographique.filter(
                  e => e.type === 'thèse'
                ).length > 0
              "
            >
              <v-col>
                <v-alert border="right" color="blue-grey" dark class="apa-norm">
                  <div
                    class="text-subtitle-1 text--secondary"
                    style="color:white !important;"
                  >
                    Thèse ou doctorat
                  </div>
                  <div
                    class="text-subtitle-2 font-italic text--secondary"
                    style="color:white !important;"
                  >
                    Nom de l’auteur·e, Initiale. (Date). Titre de la thèse
                    [Thèse de doctorat, Nom de l’établissement]. Nom de la base
                    de données. URL
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <v-row
              v-if="
                thisActivity.sourcesBibliographique.filter(
                  e => e.type === 'mémoire'
                ).length > 0
              "
            >
              <v-col>
                <v-alert border="right" color="blue-grey" dark class="apa-norm">
                  <div
                    class="text-subtitle-1 text--secondary"
                    style="color:white !important;"
                  >
                    Mémoire
                  </div>
                  <div
                    class="text-subtitle-2 font-italic text--secondary"
                    style="color:white !important;"
                  >
                    Nom de l’auteur·e, Initiale. (Date). Titre du mémoire
                    [Mémoire, Nom de l’établissement]. Nom de la base de
                    données. URL
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <v-row
              v-if="
                thisActivity.sourcesBibliographique.filter(
                  e => e.type === 'internet'
                ).length > 0
              "
            >
              <v-col>
                <v-alert border="right" color="blue-grey" dark class="apa-norm">
                  <div
                    class="text-subtitle-1 text--secondary"
                    style="color:white !important;"
                  >
                    Page internet
                  </div>
                  <div
                    class="text-subtitle-2 font-italic text--secondary"
                    style="color:white !important;"
                  >
                    Nom de l’auteur·e, Initiale. (année, jour mois). Titre du
                    document. Nom du site. Consulté le Date. URL
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-card-text>
    </v-card>
    <v-card
      elevation="0"
      v-if="
        !this.modifying && thisActivity.mediaImg && thisActivity.mediaImg.length
      "
    >
      <v-card-title>Illustrations</v-card-title>
      <v-card-text>
        <v-row v-viewer>
          <v-col
            cols="2"
            v-for="media of thisActivity.mediaImg"
            :key="media.url"
          >
            <div>
              <img :src="media.url" class="contain" />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card
      elevation="0"
      class="margin-card"
      v-if="
        !this.modifying && thisActivity.mediaPdf && thisActivity.mediaPdf.length
      "
    >
      <v-card-title>Documents</v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="2"
            v-for="media of thisActivity.mediaPdf"
            :key="media.url"
          >
            <div style="text-align:center" v-if="media.url">
              <pdf :src="media.url"></pdf>
              <v-btn
                tile
                class="mt-2"
                color="primary"
                @click="download(media.url, media.fileName)"
              >
                <v-icon left>
                  mdi-file-pdf-outline
                </v-icon>
                Télécharger
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card
      elevation="0"
      class="margin-card"
      v-if="
        !this.modifying && thisActivity.mediaVid && thisActivity.mediaVid.length
      "
    >
      <v-card-title>Vidéos</v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="2"
            v-for="media of thisActivity.mediaVid"
            :key="media.url"
          >
            <div>
              <video controls height="150" width="200">
                <source :src="media.url" type="video/mp4" />
              </video>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card elevation="0" v-if="this.modifying && thisActivity.allMedia">
      <v-card-text>
        <v-row>
          <v-col cols="3">
            <div>
              <div>
                <label class="label" :for="'illustration' + 0"
                  >Images/Vidéos/Documents</label
                >
              </div>
              <div class="frame">
                <span class="helper"></span>
                <span v-if="!thisActivity.allMedia[0]">
                  <v-icon
                    style="width: calc(100% - 10px);vertical-align: middle"
                    >mdi-plus</v-icon
                  >
                </span>
                <span v-if="thisActivity.allMedia[0]">
                  <v-progress-circular
                    v-if="thisActivity.allMedia[0].inProgress"
                    indeterminate
                    color="primary"
                    style="width: calc(100% - 10px);vertical-align: middle"
                  ></v-progress-circular>
                </span>
                <img
                  v-if="thisActivity.allMedia[0]"
                  :src="thisActivity.allMedia[0].url"
                  style="width: calc(100% - 10px);vertical-align: middle"
                />
              </div>
              <div class="subframe">
                <v-btn
                  style="vertical-align: middle;width: 100%"
                  raised
                  :class="thisActivity.allMedia[0] ? 'error' : 'primary'"
                  id="illustration0"
                  @click="
                    thisActivity.allMedia[0] ? onDelete(0) : onPickFile(0)
                  "
                  >{{
                    thisActivity.allMedia[0] ? "Supprimer" : "Ajouter"
                  }}</v-btn
                >
              </div>
              <br />

              <input
                type="file"
                style="display: none"
                :ref="'fileInput0'"
                accept="images/*"
                @change="onFilePicked($event, 0)"
              />
            </div>
          </v-col>
          <v-col
            cols="3"
            v-for="(img, index) in thisActivity.allMedia"
            :key="index"
          >
            <div style="height:22px">
              <label class="label" :for="'illustration' + 0"></label>
            </div>
            <div v-if="index + 1 < 4">
              <div class="frame">
                <span class="helper"></span>
                <span v-if="!thisActivity.allMedia[index + 1]">
                  <v-icon
                    style="width: calc(100% - 10px);vertical-align: middle"
                    >mdi-plus</v-icon
                  >
                </span>
                <span v-if="thisActivity.allMedia[index + 1]">
                  <v-progress-circular
                    v-if="thisActivity.allMedia[index + 1].inProgress"
                    indeterminate
                    color="primary"
                    style="width: calc(100% - 10px);vertical-align: middle"
                  ></v-progress-circular>
                </span>
                <img
                  v-if="thisActivity.allMedia[index + 1]"
                  :src="thisActivity.allMedia[index + 1].url"
                  style="width: calc(100% - 10px);vertical-align: middle"
                />
              </div>
              <div class="subframe">
                <v-btn
                  v-if="index + 1 < 5"
                  style="vertical-align: middle;width: 100%"
                  raised
                  :class="
                    thisActivity.allMedia[index + 1] ? 'error' : 'primary'
                  "
                  @click="
                    thisActivity.allMedia[index + 1]
                      ? onDelete(index + 1)
                      : onPickFile(index + 1)
                  "
                  >{{
                    thisActivity.allMedia[index + 1] ? "Supprimer" : "Ajouter"
                  }}</v-btn
                >
              </div>
              <br />
              <input
                type="file"
                style="display: none"
                :ref="'fileInput' + (index + 1)"
                accept=".pdf,.mp4,.jpg,.jpeg,.png"
                @change="onFilePicked($event, index + 1)"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row class="mt-2">
      <v-col align="right">
        <v-btn
          v-if="thisActivity.isMine && modifying"
          color="error"
          @click="cancelModify"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          v-if="thisActivity.isMine && modifying"
          color="success"
          @click="saveModify"
          style="margin-left:10px"
        >
          {{ $t("save") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import HeaderActivity from "@/components/ReadActivity/Header";
import { mapActions, mapGetters } from "vuex";
import pdf from "vue-pdf";
const firebase = require("../../firebase.config.js");

export default {
  name: "ReadActivity",
  components: { pdf, HeaderActivity },
  computed: {
    ...mapGetters({
      thisActivity: "activities/getThisActivity",
      mediasToUpload: "upload/getMediasToUpload",
      user: "getUser",
      average: "average"
    })
  },
  created() {
    this.id = this.$route.params.id;
    this.fetchActivity(this.id);
  },
  methods: {
    ...mapActions({
      fetchActivity: "activities/fetchActivity",
      delete: "activities/deleteActivity",
      restoreData: "activities/restoreData",
      updateActivity: "activities/updateActivity",
      uploadMedias: "upload/uploadMedias",
      resetUpload: "upload/resetUpload"
    }),
    modify() {
      this.thisActivityOriginal = JSON.parse(JSON.stringify(this.thisActivity));
      this.modifying = true;
    },
    urlify(text) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      return text.replace(urlRegex, function(url) {
        return '<a href="' + url + '">' + url + "</a>";
      });
    },
    deleteSource: function(index) {
      this.thisActivity.sourcesBibliographique.splice(index, 1);
    },
    addSource(type) {
      this.thisActivity.sourcesBibliographique.push({
        type: type,
        source: ""
      });
    },
    getIconByType(type) {
      switch (type) {
        case "article":
          return "mdi-newspaper-variant-outline ";
        case "internet":
          return "mdi-web";
        case "livre":
          return "mdi-book-open-outline ";
        case "chapitre":
          return "mdi-book-outline";
        case "thèse":
          return "mdi-school-outline";
        case "mémoire":
          return "mdi-notebook-outline";
      }
    },
    async saveModify() {
      this.overlay = true;
      if (this.thisActivity.allMedia && this.thisActivity.allMedia.length > 0) {
        // eslint-disable-next-line no-unused-vars
        for (const [index, image] of this.thisActivity.allMedia.entries()) {
          const number = index + 1;
          //if(!this.thisActivity.medias.includes(image.fileName)) {
          this.loadingText = "Upload du document n°" + number + "...";
          await this.uploadMedias({ image: image, cover: false });
          // }
        }
      }
      this.loadingText = "Ajout des données...";
      await this.updateActivity(this.id);
      this.resetUpload();
      this.loadingText = "Sauvegarde...";
      this.overlay = false;
      this.loadingText = "";
      await this.fetchActivity(this.id);
      this.modifying = false;
    },
    cancelModify() {
      this.restoreData(this.thisActivityOriginal);
      this.modifying = false;
    },
    onDelete(index) {
      this.thisActivity.allMedia.splice(index, 1);
    },
    async onFilePicked(event, index) {
      const files = [...event.target.files];
      let filename = files[0].name;
      if (filename.lastIndexOf(".") <= 0) {
        return alert("Veuillez entrer un fichier valide");
      }
      let fileExtension = filename.substr(
        filename.lastIndexOf(".") + 1,
        filename.length
      );
      if (!this.allowedExtension.includes(fileExtension.toLowerCase())) {
        alert("Extension de fichier non autorisé");
        return false;
      }
      const fileReader = new FileReader();
      fileReader.addEventListener("load", () => {
        this.imageUrl = fileReader.result;
        this.thisActivity.allMedia[index].url = this.imageUrl;
        this.thisActivity.allMedia[index].inProgress = false;
      });
      await fileReader.readAsDataURL(files[0]);
      this.imgToUpload = files[0];
      let response = {};
      response.imageUrl = this.imageUrl;
      response.file = this.imgToUpload;
      this.thisActivity.allMedia.push({
        url: this.imageUrl,
        file: this.imgToUpload,
        inProgress: true
      });
    },
    onPickFile(index) {
      if (Array.isArray(this.$refs["fileInput" + index])) {
        this.$refs["fileInput" + index][0].click();
      } else {
        this.$refs["fileInput" + index].click();
      }
    },
    displayDesignButton(type) {
      return ["article", "thèse", "mémoire"].includes(type);
    },
    async download(fileURL, fileName) {
      const xhr = new XMLHttpRequest();
      xhr.responseType = "blob";
      xhr.onload = function() {
        const blob = xhr.response;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      };
      xhr.open("GET", fileURL);
      xhr.send();
    },
    deleteActivity() {
      this.confirm = false;
      this.delete(this.id);
      this.$router.push("/");
    },
    deleteUser: function(index) {
      this.thisActivity.etapes.splice(index, 1);
      if (index === 0) this.addUser();
    },
    addUser: function(index) {
      if (index < 10) {
        this.thisActivity.etapes.push("");
      }
    }
  },
  data: () => ({
    logoCC: require("@/assets/creative-commons-icon.svg"),
    logoCCBY: require("@/assets/creative-commons-by-icon.svg"),
    logoCCSA: require("@/assets/creative-commons-sa-icon.svg"),
    sheet: false,
    firebase: firebase.configOptions,
    confirm: false,
    modifying: false,
    overlay: false,
    loadingText: "",
    allowedExtension: ["png", "jpg", "jpeg", "mp4", "pdf"],
    images: [],
    basicRules: [v => !!v || "Ce champ est requis"],
    fonctions: [
      { name: "Fonctions attentionnelles" },
      { name: "Mémoire à court terme et/ou de travail" },
      { name: "Mémoire à long terme (épisodique, sémantiques, procédurale)" },
      {
        name:
          "Fonctions exécutives (flexibilité, planification, inhibition, mise à jour)"
      },
      { name: "Gnosies" },
      { name: "Capacités visuo-spatiales et constructives" },
      { name: "Praxies" },
      { name: "Vitesse de traitement" },
      { name: "Métacognition" },
      { name: "Cognition sociale" },
      { name: "Vie quotidienne" }
    ],
    procedures: [
      "Amélioration des fonctions déficitaires",
      "Optimisation des fonctions préservées",
      "Aménagement de l’environnement"
    ],
    designs: [
      "Etude de cas",
      "Etude de cohorte",
      "Guide de pratique clinique",
      "Synthèse clinique",
      "Revue systématique",
      "Méta-analyse",
      "Essai contrôlé randomisé",
      "Etude comparative randomisée",
      "Etude retrospective",
      "Série de cas"
    ],
    tranchesAges: [
      "3 à 6 ans",
      "7 à 10 ans",
      "11 à 13 ans",
      "14 à 17 ans",
      "18 ans et plus",
      "18 à 25 ans",
      "26 à 40 ans",
      "41 à 65 ans",
      "66 à 80 ans",
      "81 ans et plus"
    ],
    activiteeConsultationOriginal: {}
  })
};
</script>
<style scoped>
img {
  width: 200px;
  height: 150px;
  border: 1px solid #000;
}

.contain {
  object-fit: contain;
}

.courseHeader {
  color: #fff;
  padding: 5px 0 0;
  line-height: 30px;
  background: #7451eb;
  margin-bottom: 16px;
  position: relative;
}

.grid-wrapper {
  margin: auto;
  max-width: 1280px;
  width: 100%;
}

.apa-norm {
  margin-bottom: -11px !important;
  padding: 12px !important;
}

.v-card.margin-card {
  border-radius: 6px;
  margin-top: 30px;
  margin-bottom: 15px;
}
img {
  border: 0px !important;
}
.frame {
  height: 150px; /* equals max image height */
  width: 160px;
  border: 1px cadetblue solid;
  white-space: nowrap;

  text-align: center;
  margin: 1em 0;
}
.subframe {
  height: 39px; /* equals max image height */
  width: 160px;
  border: 1px cadetblue solid;
  white-space: nowrap;

  text-align: center;
  margin: 1em 0;
}
.helper {
  display: inline-block;
  height: 100%;
  vertical-align: middle;
}
</style>
